import { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftButton from "components/SoftButton";
import { useAuth0 } from "@auth0/auth0-react";
import "../NewIntegrationDialog/styles.css";
import { FormStep } from "../NewIntegrationDialog/Steps/Form";
import SoftBox from "components/SoftBox";
import {
  setIntegrationMutatingFlag,
  updateIntegration
} from "redux/actions/portal/integrationActionCreator";
import { uploadFile } from "helpers/common";
import SoftTypography from "components/SoftTypography";
import Checkbox from "@mui/material/Checkbox";
import { DeleteIntegrationDialog } from "cxfcomponents/Integrations/DeleteIntegrationDialog";
import { deleteIntegration } from "redux/actions/portal/integrationActionCreator";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

export const UpdateIntegrationDialog = ({ open, onClose, integration }) => {
  const { user } = useAuth0();
  const { t } = useTranslation("flowDesigner");
  const { integrationMutatingFlag } = useSelector((store) => store?.portal?.integrations);
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [pictureUpload, setPictureUpload] = useState(null);
  const [checkDeleteIntegration, setCheckDeleteIntegration] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const navigate = useNavigate();
  const { slug } = useParams();

  const handleFormStepSubmit = async (values) => {
    if (!user?.stripeId || !user?.cxfUserId) return;

    const updatedIntegration = {
      tenant_id: user.stripeId,
      create_user_id: user.cxfUserId,
      modified_user_id: user.cxfUserId,
      flows: [],
      id: integration.id,
      ...values,
      integration_name: values.integrationName,
      logo: integration.logo
    };

    try {
      if (pictureUpload) {
        const result = await uploadFile(user, pictureUpload, "integrations", integration.id, true);
        updatedIntegration.logo = result?.Location;
      } else if (pictureUpload === "") {
        updatedIntegration.logo = "";
      }

      dispatch(setIntegrationMutatingFlag(true));
      dispatch(updateIntegration(updatedIntegration));
      setTimeout(() => {
        resetNewIntegration();
      }, 1000);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save the integration");
    }
  };

  const resetNewIntegration = () => {
    onClose();
    setPictureUpload(null);
    setCheckDeleteIntegration(false);
  };

  const handleDialogClose = () => {
    resetNewIntegration();
  };

  const handleAcceptDeletion = () => {
    if (!user.stripeId || integrationMutatingFlag) return;

    try {
      dispatch(setIntegrationMutatingFlag(true));
      dispatch(
        deleteIntegration({
          tenant_id: user.stripeId,
          integration_id: integration.id,
          onSuccess: slug
            ? () => {
                navigate("/workspace/integrations");
              }
            : undefined
        })
      );
    } catch (error) {
      toast.error("Failed to delete the integration");
    }
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={"csm"}
        open={open}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          data-testid="form-title"
          sx={{
            marginBottom: "20px",
            padding: "32px 0 0 48px"
          }}
          className="white-to-dark"
        >
          Update Integration
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "0 48px 24px"
          }}
        >
          <SoftBox>
            <FormStep
              ref={formRef}
              handleFormStepSubmit={handleFormStepSubmit}
              initialValues={{
                integrationName: integration.integration_name,
                description: integration.description ?? "",
                version: integration.current_version
              }}
              pictureUpload={pictureUpload}
              integration={integration}
              setPictureUpload={setPictureUpload}
            />
          </SoftBox>
          <SoftBox
            className="border-top"
            sx={{
              paddingTop: "20px"
            }}
          >
            <SoftTypography
              sx={{
                fontSize: "14px",
                marginBottom: "8px"
              }}
            >
              Deleting forever will permanently delete this integration and it cannot be retrieved.
              Check the box to confirm deleting forever.
            </SoftTypography>
            <SoftBox
              sx={{
                alignItems: "flex-start",
                display: "flex",
                marginBottom: "14px"
              }}
            >
              <Checkbox
                id="confirmDelete"
                style={{
                  marginRight: "20px"
                }}
                checked={checkDeleteIntegration}
                onChange={(event) => {
                  setCheckDeleteIntegration(event.target.checked);
                }}
                // disabled={isReadOnlyAccess}
              />
              <SoftTypography
                component="label"
                htmlFor="confirmDelete"
                sx={{
                  fontSize: "12px"
                }}
              >
                I’m sure I want to delete this integration.
              </SoftTypography>
            </SoftBox>
            <SoftButton
              sx={{ backgroundColor: "#E53E3E !important" }}
              color="error"
              onClick={async () => {
                setShowDeleteDialog(true);
              }}
              disabled={!checkDeleteIntegration}
            >
              delete forever
            </SoftButton>
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <SoftButton
            disabled={!!integrationMutatingFlag}
            variant="outlined"
            color="info"
            onClick={handleDialogClose}
          >
            {t("buttons.cancel")}
          </SoftButton>
          <SoftButton
            disabled={!!integrationMutatingFlag}
            color="primary"
            onClick={() => formRef.current?.submitForm()}
          >
            Update
          </SoftButton>
        </DialogActions>
      </Dialog>
      <DeleteIntegrationDialog
        open={showDeleteDialog}
        onAccept={handleAcceptDeletion}
        onClose={() => setShowDeleteDialog(false)}
        integration={integration}
      />
    </>
  );
};
